import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { isStaff, isSuperAdmin } from '@foundry/shared';
import { useLogout, useMe } from '../api/auth.js';
import { Wordmark } from './Logo.js';

interface NavItem {
  to: string;
  label: string;
}

/**
 * A top-nav dropdown grouping several destinations under one heading, to keep the
 * bar uncluttered. Opens on click; closes on an outside click, on Escape, and
 * whenever the route changes. The heading highlights while one of its items is
 * the active route.
 */
function NavGroup({ label, items }: { label: string; items: NavItem[] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Any navigation closes the menu.
  useEffect(() => setOpen(false), [location.pathname]);

  const active = items.some(
    (it) => location.pathname === it.to || location.pathname.startsWith(`${it.to}/`),
  );

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium transition ${
          active ? 'bg-primary text-white' : 'hover:bg-neutral'
        }`}
      >
        {label}
        <span className="text-[0.6rem] opacity-70">▼</span>
      </button>
      {open ? (
        <div className="absolute left-0 z-20 mt-1 min-w-44 rounded-md border border-secondary/10 bg-white py-1 shadow-lg">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end
              className={({ isActive }) =>
                `block px-3 py-1.5 text-sm ${isActive ? 'bg-primary text-white' : 'hover:bg-neutral'}`
              }
            >
              {it.label}
            </NavLink>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** The signed-in application shell: brand header, role-aware nav, sign-out. */
export function Layout() {
  const { data: user } = useMe();
  const logout = useLogout();
  const navigate = useNavigate();

  const navClass = ({ isActive }: { isActive: boolean }): string =>
    `rounded-md px-3 py-1.5 text-sm font-medium transition ${
      isActive ? 'bg-primary text-white' : 'hover:bg-neutral'
    }`;

  const staff = Boolean(user && isStaff(user.role));
  const superAdmin = Boolean(user && isSuperAdmin(user.role));

  // Grouped staff destinations. Super-admin-only items are filtered out for
  // developers, and a group with nothing left in it is not rendered.
  const staffGroups: { label: string; items: NavItem[] }[] = [
    {
      label: 'Project Build',
      items: [
        { to: '/console/monitor', label: 'Monitor' },
        { to: '/console/debug', label: 'Debug' },
      ],
    },
    ...(superAdmin
      ? [
          {
            label: 'User Management',
            items: [
              { to: '/console/people', label: 'Invitations' },
              { to: '/console/users', label: 'Users' },
            ],
          },
        ]
      : []),
    {
      label: 'Global Settings',
      items: [
        ...(superAdmin
          ? [
              { to: '/console/tiers', label: 'Tiers' },
              { to: '/console/secrets', label: 'Keys' },
            ]
          : []),
        { to: '/console/audit', label: 'Audit' },
        { to: '/settings', label: 'Settings' },
      ],
    },
  ];

  return (
    <div className="min-h-screen">
      <header className="border-b border-secondary/10 bg-white">
        <div className="mx-auto flex max-w-5xl items-center gap-4 px-6 py-3">
          <NavLink to={staff ? '/console' : '/'} className="flex items-center">
            <Wordmark className="h-9 w-auto" />
          </NavLink>
          <nav className="flex items-center gap-1">
            {staff ? (
              <>
                {/* Staff work across every founder's project. */}
                <NavLink to="/console" end className={navClass}>
                  Projects
                </NavLink>
                {staffGroups.map((g) => (
                  <NavGroup key={g.label} label={g.label} items={g.items} />
                ))}
              </>
            ) : (
              <>
                <NavLink to="/" end className={navClass}>
                  Projects
                </NavLink>
                <NavLink to="/membership" className={navClass}>
                  Membership
                </NavLink>
                <NavLink to="/settings" className={navClass}>
                  Settings
                </NavLink>
              </>
            )}
          </nav>
          <div className="ml-auto flex items-center gap-3 text-sm">
            {user ? <span className="opacity-70">{user.email}</span> : null}
            <button
              className="rounded-md px-3 py-1.5 font-medium ring-1 ring-secondary/20 hover:bg-neutral disabled:opacity-50"
              disabled={logout.isPending}
              onClick={() => logout.mutate(undefined, { onSuccess: () => void navigate('/login') })}
            >
              Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-6 py-8">
        <Outlet />
      </main>
    </div>
  );
}
